import { useEffect, useMemo, useState } from "react";
import { FileText, RefreshCw, Calendar, Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import api from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));

const formatRelative = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
};

const STATUS_PILLS = {
  green: { bg: "#d1fae5", color: "#065f46", border: "#34d399", label: "Activo" },
  amber: { bg: "#fef3c7", color: "#92400e", border: "#fbbf24", label: "Atención" },
  red:   { bg: "#fee2e2", color: "#991b1b", border: "#f87171", label: "Inactivo" },
};

const FILTERS = [
  { id: "all",   label: "Todos" },
  { id: "green", label: "Activos" },
  { id: "amber", label: "Atención" },
  { id: "red",   label: "Inactivos" },
];

const PAGE_SIZES = [10, 25, 50];

export default function FormsPerformanceCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("total_leads");
  const [sortDir, setSortDir] = useState("desc");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    api.get("/leads-digital/forms-performance")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);
  useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

  // Derived: counts by status (over all forms)
  const counts = useMemo(() => {
    if (!data) return { green: 0, amber: 0, red: 0, total_leads: 0 };
    return data.forms.reduce(
      (acc, f) => {
        const s = f.status || "amber";
        acc[s] = (acc[s] || 0) + 1;
        acc.total_leads += f.total_leads || 0;
        return acc;
      },
      { green: 0, amber: 0, red: 0, total_leads: 0 },
    );
  }, [data]);

  // Derived: filtered + sorted list
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    let arr = data.forms.filter((f) => {
      if (statusFilter !== "all" && (f.status || "amber") !== statusFilter) return false;
      if (q && !String(f.wp_form_name || "").toLowerCase().includes(q)) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    });
    return arr;
  }, [data, search, statusFilter, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageRows = filtered.slice(pageStart, pageStart + pageSize);
  const showingFrom = filtered.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(filtered.length, pageStart + pageSize);

  const setSort = (col) => {
    if (col === sortBy) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
  };

  const clearFilters = () => { setSearch(""); setStatusFilter("all"); };

  if (loading || !data) {
    return (
      <section className="surface" data-testid="forms-performance-loading">
        <div className="flex justify-center py-12"><div className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}/></div>
      </section>
    );
  }

  return (
    <section className="surface overflow-hidden" data-testid="forms-performance-card">
      {/* Header */}
      <div className="flex items-start justify-between p-6 border-b border-gray-100 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="kpi-icon-clean" style={{ color: "#0ea5e9", boxShadow: "inset 0 0 0 1px #0ea5e91f" }}>
            <FileText size={18}/>
          </div>
          <div>
            <h3 className="font-display font-extrabold text-lg">Form Performance</h3>
            <p className="text-xs text-gray-500 font-semibold mt-0.5">
              Detección automática de formularios WP · estado · calidad de captura por form
            </p>
          </div>
        </div>
        <button className="btn flex items-center gap-2" onClick={load} data-testid="forms-perf-refresh">
          <RefreshCw size={14}/> Refrescar
        </button>
      </div>

      {/* Mini KPI bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-100" data-testid="forms-kpis">
        <KpiCell label="Activos"    value={counts.green} color="#16a34a"/>
        <KpiCell label="Atención"   value={counts.amber} color="#f59e0b"/>
        <KpiCell label="Inactivos"  value={counts.red}   color="#dc2626"/>
        <KpiCell label="Total Leads" value={fmt(counts.total_leads)} color="#111827"/>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between p-4 border-b border-gray-100 bg-white">
        <div className="relative w-full sm:max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar formulario por nombre…"
            className="pl-9 pr-3 py-2 w-full rounded-lg border border-gray-200 bg-white text-sm font-semibold focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
            data-testid="forms-search-input"
          />
        </div>
        <div className="flex bg-gray-100 p-1 rounded-lg gap-1 flex-wrap" role="tablist" aria-label="Filtro por estado">
          {FILTERS.map((f) => {
            const active = statusFilter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                className="px-3 py-1.5 rounded-md text-xs font-extrabold transition-colors"
                style={{
                  background: active ? "#ffffff" : "transparent",
                  color: active ? "#111827" : "#6b7280",
                  boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                }}
                data-testid={`forms-filter-${f.id}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState onClear={clearFilters} hasFilters={search !== "" || statusFilter !== "all"}/>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="forms-table">
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  <th className="text-left text-[10px] font-extrabold uppercase tracking-wider text-gray-500 py-3 pl-4" style={{ width: 28 }}></th>
                  <SortableTh active={sortBy === "wp_form_name"} dir={sortDir} onClick={() => setSort("wp_form_name")} testid="th-name">Formulario</SortableTh>
                  <SortableTh active={sortBy === "total_leads"} dir={sortDir} onClick={() => setSort("total_leads")} align="right" testid="th-leads">Leads</SortableTh>
                  <th className="text-left text-[10px] font-extrabold uppercase tracking-wider text-gray-500 py-3">Estado</th>
                  <SortableTh active={sortBy === "last_seen"} dir={sortDir} onClick={() => setSort("last_seen")} testid="th-last">Último</SortableTh>
                  <th className="text-left text-[10px] font-extrabold uppercase tracking-wider text-gray-500 py-3">Canal top</th>
                  <SortableTh active={sortBy === "model_rate"} dir={sortDir} onClick={() => setSort("model_rate")} align="right" testid="th-model">Modelo</SortableTh>
                  <SortableTh active={sortBy === "branch_rate"} dir={sortDir} onClick={() => setSort("branch_rate")} align="right" testid="th-branch">Sucursal</SortableTh>
                  <SortableTh active={sortBy === "utm_rate"} dir={sortDir} onClick={() => setSort("utm_rate")} align="right" testid="th-utm">UTM</SortableTh>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((f) => {
                  const pill = STATUS_PILLS[f.status] || STATUS_PILLS.amber;
                  return (
                    <tr key={f.wp_form_name + (f.wp_form_id || "")}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                        className="hover:bg-gray-50/80 transition-colors"
                        data-testid={`form-row-${f.wp_form_name}`}>
                      <td className="py-3 pl-4"><span className="health-dot" style={{ background: pill.color, display: "inline-block" }}/></td>
                      <td className="py-3">
                        <strong className="block font-display font-extrabold">{f.wp_form_name}</strong>
                        {f.wp_form_id && <span className="text-[11px] text-gray-500 font-semibold">ID: {f.wp_form_id}</span>}
                      </td>
                      <td className="py-3 text-right font-display font-extrabold" style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(f.total_leads)}</td>
                      <td className="py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold"
                              style={{ background: pill.bg, color: pill.color, border: `1px solid ${pill.border}` }}>
                          {pill.label}
                        </span>
                      </td>
                      <td className="py-3 text-xs">
                        <span className="flex items-center gap-1 text-gray-600 font-semibold">
                          <Calendar size={11}/> {formatRelative(f.last_seen)}
                        </span>
                      </td>
                      <td className="py-3 text-xs">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold"
                              style={{ background: "#f3f4f6", color: "#1f2937" }}>
                          {f.top_channel || "—"}
                        </span>
                      </td>
                      <td className="py-3 text-right"><RatePill v={f.model_rate}/></td>
                      <td className="py-3 text-right"><RatePill v={f.branch_rate}/></td>
                      <td className="py-3 text-right pr-4"><RatePill v={f.utm_rate}/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t border-gray-100 gap-3" data-testid="forms-pagination">
            <div className="flex items-center gap-2 text-xs font-bold text-gray-500">
              <span>Mostrando {showingFrom}–{showingTo} de {filtered.length}</span>
              <span className="mx-2 text-gray-300">·</span>
              <span>Filas:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 py-1 rounded-md border border-gray-200 text-xs font-bold focus:outline-none focus:border-amber-400"
                data-testid="forms-page-size"
              >
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-md text-xs font-extrabold flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                data-testid="pagination-prev"
              >
                <ChevronLeft size={14}/> Anterior
              </button>
              <span className="px-3 py-1.5 text-xs font-extrabold text-gray-700" data-testid="pagination-info">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-md text-xs font-extrabold flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                data-testid="pagination-next"
              >
                Siguiente <ChevronRight size={14}/>
              </button>
            </div>
          </div>
        </>
      )}

      {data.unknown_total > 0 && (
        <div className="px-6 py-3 text-xs font-semibold flex items-start gap-2 border-t border-amber-100"
             style={{ background: "#fef3c7", color: "#78350f" }}>
          <div>
            <strong>{fmt(data.unknown_total)} leads</strong> sin <code className="px-1.5 py-0.5 rounded bg-amber-200/50">wp_form_name</code> identificado.
            Añade <code className="px-1.5 py-0.5 rounded bg-amber-200/50">wp_form_name</code> al mapeo de tu Zap para auditarlos aquí.
          </div>
        </div>
      )}
    </section>
  );
}

function KpiCell({ label, value, color }) {
  return (
    <div className="px-5 py-4 bg-white" data-testid={`forms-kpi-${label}`}>
      <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">{label}</div>
      <div className="font-display font-extrabold mt-1" style={{ fontSize: 22, color, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

function SortableTh({ children, active, dir, onClick, align = "left", testid }) {
  return (
    <th
      onClick={onClick}
      className="cursor-pointer select-none py-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-500 hover:text-gray-900"
      style={{ textAlign: align }}
      data-testid={testid}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`} style={{ color: active ? "#111827" : undefined }}>
        {children}
        {active && (dir === "asc" ? <ChevronUp size={11}/> : <ChevronDown size={11}/>)}
      </span>
    </th>
  );
}

function RatePill({ v }) {
  const bg = v >= 85 ? "#dcfce7" : v >= 65 ? "#fef3c7" : "#fee2e2";
  const color = v >= 85 ? "#166534" : v >= 65 ? "#92400e" : "#991b1b";
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold" style={{ background: bg, color, fontVariantNumeric: "tabular-nums" }}>{v}%</span>;
}

function EmptyState({ hasFilters, onClear }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center px-6" data-testid="forms-empty">
      <div className="kpi-icon-clean mb-3" style={{ width: 48, height: 48, color: "#9ca3af", boxShadow: "inset 0 0 0 1px #9ca3af1f" }}>
        <FileText size={22}/>
      </div>
      <div className="font-display font-extrabold text-base mb-1">
        {hasFilters ? "Sin resultados para los filtros aplicados" : "Aún no se han detectado formularios"}
      </div>
      <p className="text-sm text-gray-500 font-semibold max-w-md mb-4">
        {hasFilters
          ? "Prueba con otros criterios o limpia los filtros para ver todos los formularios."
          : "Asegúrate de que tu webhook envíe el campo wp_form_name."}
      </p>
      {hasFilters && (
        <button onClick={onClear} className="px-4 py-2 rounded-lg text-xs font-extrabold flex items-center gap-1.5"
                style={{ background: "#111827", color: "#f4c400" }}
                data-testid="forms-clear-filters">
          <X size={12}/> Borrar Filtros
        </button>
      )}
    </div>
  );
}
